/**
 * Lokale Datenbank der Kasse (IndexedDB). Ein Verkauf gilt erst als bezahlt, wenn seine Transaktion abgeschlossen ist
 * (`durability: 'strict'`), siehe POS-07. Ein aktiver Tab je Kasse (Web Locks), damit nichts doppelt gezählt wird.
 */
export const DB_NAME = 'barso-pos';
/**
 * 2: Index `altCode` für fremde Codes (Migration 0036 auf dem Server).
 * 3: Index `sellerItem` (Verkäufernummer, Artikelnummer) für die Eingabe „17/5“ (O3), Store `parked` für geparkte Körbe
 * (X4); der Snapshot-Cursor wird gelöscht, damit der nächste Abgleich vollständig lädt und `itemNo` nachträgt.
 */
export const DB_VERSION = 4;
/**
 * 4 (U17f/U17h): Stores `notes` (Kommentare der Kasse, Schlüssel `clientUuid`) und `photos` (Fotos zu Artikeln ohne Etikett,
 * Schlüssel `photoId`). Nur neue Stores: Outbox, Stornos, Verlauf, Geparktes und Cursor bleiben unberührt.
 */

export type StoreName =
  'meta' | 'items' | 'outbox' | 'voids' | 'history' | 'parked' | 'notes' | 'photos';

export function openPosDb(name: string = DB_NAME): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(name, DB_VERSION);
    request.onupgradeneeded = (event) => {
      const db = request.result;
      // Schrittweise je Version: eine Kasse mit offenen Verkäufen in Version 1 behält alle Daten
      if (event.oldVersion < 1) {
        db.createObjectStore('meta');
        const items = db.createObjectStore('items', { keyPath: 'id' });
        items.createIndex('code', 'code', { unique: true });
        const outbox = db.createObjectStore('outbox', { keyPath: 'clientUuid' });
        outbox.createIndex('seq', 'registerSeq');
        db.createObjectStore('voids', { keyPath: 'key' });
        const history = db.createObjectStore('history', { keyPath: 'clientUuid' });
        history.createIndex('at', 'soldAtMs');
      }
      if (event.oldVersion < 2) {
        // Nicht eindeutig: der Server sichert die Eindeutigkeit je Basar; Artikel ohne fremden Code fehlen im Index
        const transaction = request.transaction;
        if (transaction === null) throw new Error('Upgrade ohne Transaktion');
        transaction.objectStore('items').createIndex('altCode', 'altCode', { unique: false });
      }
      if (event.oldVersion < 3) {
        const transaction = request.transaction;
        if (transaction === null) throw new Error('Upgrade ohne Transaktion');
        // Nicht eindeutig; Artikel ohne `itemNo` (Stand vor dem Update) fehlen im Index, bis der Snapshot neu geladen ist
        transaction
          .objectStore('items')
          .createIndex('sellerItem', ['sellerNumber', 'itemNo'], { unique: false });
        db.createObjectStore('parked', { keyPath: 'parkNo' });
        // Nur ein vorhandener Cursor wird gelöscht (bei einer neuen Datenbank gibt es keinen); Outbox, Stornos, Verlauf
        // und Zähler bleiben unberührt
        transaction.objectStore('meta').delete('cursor');
      }
      if (event.oldVersion < 4) {
        db.createObjectStore('notes', { keyPath: 'clientUuid' });
        db.createObjectStore('photos', { keyPath: 'photoId' });
      }
    };
    let rejected = false;
    request.onsuccess = () => {
      const db = request.result;
      // Kam die Verbindung erst nach einer Absage (anderer Tab blockierte), niemand hält sie: schließen
      if (rejected) {
        db.close();
        return;
      }
      // Ein späteres Update in einem anderen Tab darf nicht an dieser Verbindung hängen bleiben
      db.onversionchange = () => {
        db.close();
      };
      resolve(db);
    };
    request.onerror = () => {
      reject(request.error ?? new Error('IndexedDB nicht verfügbar'));
    };
    request.onblocked = () => {
      rejected = true;
      reject(
        new Error(
          'Die Kasse ist in einem anderen Tab mit einer älteren Version geöffnet. Bitte andere BARSO-Kassen-Tabs schließen und neu laden; offene Verkäufe bleiben erhalten.',
        ),
      );
    };
  });
}

/** Promise zu einer IndexedDB-Anfrage. */
export function req<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onerror = () => {
      reject(request.error ?? new Error('IndexedDB-Fehler'));
    };
  });
}

export type Stores = Record<StoreName, IDBObjectStore>;

/**
 * Führt Lese- und Schreibzugriffe in EINER Transaktion aus und wartet, bis sie abgeschlossen ist. Wirft `work`, wird alles
 * zurückgerollt. In `work` darf nur auf IndexedDB gewartet werden (sonst schließt der Browser die Transaktion).
 */
export async function transact<T>(
  db: IDBDatabase,
  names: readonly StoreName[],
  mode: IDBTransactionMode,
  work: (stores: Stores) => Promise<T>,
): Promise<T> {
  const transaction = db.transaction([...names], mode, { durability: 'strict' });
  const done = new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => {
      resolve();
    };
    transaction.onerror = () => {
      reject(transaction.error ?? new Error('IndexedDB-Fehler'));
    };
    transaction.onabort = () => {
      reject(transaction.error ?? new Error('IndexedDB-Transaktion abgebrochen'));
    };
  });
  const stores = Object.fromEntries(
    names.map((name) => [name, transaction.objectStore(name)]),
  ) as Stores;
  let result: T;
  try {
    result = await work(stores);
  } catch (error) {
    try {
      transaction.abort();
    } catch {
      // schon beendet
    }
    await done.catch(() => undefined);
    throw error;
  }
  await done;
  return result;
}

/**
 * Nur ein Tab darf die Kasse führen: zwei Tabs würden dieselben Nummern vergeben. Gibt eine Freigabe-Funktion zurück oder
 * `null`, wenn ein anderer Tab die Kasse hält. Ohne Web-Locks-Unterstützung wird nicht geprüft.
 */
export function tryAcquireRegisterLock(name: string): Promise<(() => void) | null> {
  const locks: LockManager | undefined = 'locks' in navigator ? navigator.locks : undefined;
  if (locks === undefined) return Promise.resolve(() => undefined);
  return new Promise((resolveOuter) => {
    void locks.request(`barso-pos-${name}`, { ifAvailable: true }, (lock) => {
      if (lock === null) {
        resolveOuter(null);
        return undefined;
      }
      return new Promise<void>((release) => {
        resolveOuter(() => {
          release();
        });
      });
    });
  });
}
